import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { OnoffType } from '@/api/enum/common.enum';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { useParams } from 'react-router-dom';
import {
	HealthDetailListFilter,
	HealthExerciseDTO,
} from '@/api/DTO/app-setting/health.interface';
import {
	useCreateHealthDetailListMutation,
	useGetHealthDetailListQuery,
} from '@/api/app-setting/healthApiSlice';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';

interface Props {}

export default function AddExerciseDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const { healthId } = useParams();
	const [createHealthDetailList] = useCreateHealthDetailListMutation();
	const [selectedExercise, setSelectedExercise] = useState<any[]>([]);

	const exerciseTypeList = [
		{
			id: 1,
			name: t('moment.meditation'),
		},
		{
			id: 2,
			name: t('moment.body'),
		},
		{
			id: 3,
			name: t('moment.crossover'),
		},
	];

	const onoffList = [
		{
			id: OnoffType.off,
			name: t('moment.off'),
		},
		{
			id: OnoffType.on,
			name: t('moment.on'),
		},
	];

	const defaultPropertyFilter: HealthDetailListFilter = {
		pagination: initialPagination,
		id: healthId || '',
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<HealthDetailListFilter> =
		defaultPropertyFilter;
	const methods = useForm<HealthDetailListFilter>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetHealthDetailListQuery(methods.getValues(), {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const { data: allData } = useGetHealthDetailListQuery(
		{
			pagination: {
				page: 1,
				size: 999999,
			},
			id: healthId || '',
			searchString: '',
			sortings: [],
		},
		{
			refetchOnMountOrArgChange: true,
			skip: !open,
		}
	);

	useEffect(() => {
		if (allData) {
			const selectedRowsData = allData?.data?.items.filter((row) => row.id);
			setSelectedExercise(selectedRowsData);
		}
	}, [allData]);

	useEffect(() => {}, [methods.watch()]);
	const onSubmit = async () => {
		if (selectedExercise.length < 1) {
			enqueueSnackbar(t('snackbar.pleaseSelectRows'), {
				variant: 'warning',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		} else {
			const arr = selectedExercise.map((item) => {
				return item.exerciseId;
			});
			const response = await createHealthDetailList({
				arr,
				id: healthId || '',
			});
			if ('data' in response) {
				enqueueSnackbar(t('snackbar.addMomentDetailExerciseSuccess'), {
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});
				handleClose();
			}
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('moment.exerciseTitleZh'),
			flex: 21,
		},
		{
			field: 'exerciseType',
			headerName: t('moment.exerciseType'),
			flex: 21,
			renderCell: (params: any) =>
				showLabel(params.row.exerciseType, exerciseTypeList),
		},
		{
			field: 'seriesTitleZh',
			headerName: t('moment.seriesTitleZh'),
			flex: 21,
		},
		{
			field: 'exerciseTime',
			headerName: t('moment.seriesTime'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('moment.pushStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.published, onoffList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('moment.lastUpdateTime'),
			flex: 21,
		},
		{
			field: 'createdAt',
			headerName: t('moment.createTime'),
			flex: 21,
		},
	];

	const handlechange = (text: string) => {
		methods.reset({ ...defaultPropertyFilter, searchString: text });
	};

	const handleSelect = (rows: string[]) => {
		console.log(rows);
		const selectedRowsData = allData?.data?.items.filter((row) =>
			rows.includes(row.exerciseId || '')
		);
		console.log(selectedRowsData);
		setSelectedExercise(selectedRowsData || []);
	};

	const transformResponse = useCallback((response?: HealthExerciseDTO[]) => {
		if (!response) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				id: item.seriesId,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<AddCircleOutlineIcon />}
				onClick={() => setOpen(true)}
			>
				{t('moment.addExercise')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('moment.addExercise')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<AppSummarySearchBar
							name='searchString'
							control={methods.control}
							placeholder={t('common.search')}
							onchange={(text) => {
								handlechange(text);
							}}
						/>
						<div style={{ height: '20px' }}></div>
						<AppDataGrid
							checkboxSelection
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.exerciseId}
							onSelectionModelChange={(selectionModel) => {
								handleSelect(selectionModel as string[]);
							}}
							selectionModel={selectedExercise.map(
								(item: { exerciseId: any }) => item.exerciseId
							)}
						/>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.add')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
